import React, {
    cloneElement,
    ComponentProps,
    ReactElement,
    FocusEvent,
    useCallback,
    useRef,
    useState,
} from 'react';
import uniqueId from 'lodash/uniqueId';
import styled, { css } from 'styled-components';
import { shouldIgnoreProp } from '../../utils/shouldIgnoreProp';
import { WithHint } from '../feedback/Tooltip';
import { Box } from '../layout/Box';
import { Flex, type FlexProps } from '../layout/Flex';
import { Typography, TypographyProps } from '../typography/Typography';

interface LabelProps extends TypographyProps {
    required?: boolean;
}

const Label = styled(Typography)
    .withConfig({
        displayName: 'Label',
        shouldForwardProp: shouldIgnoreProp('required'),
    })
    .attrs<LabelProps>((props) => ({ ...props, variant: 'caption' }))<LabelProps>`
  user-select: none;

  ${(props) =>
      props.required &&
      css`
          &::after {
              content: '*';
              margin-left: 0.125em;
          }
      `}
`;

interface HelperTextProps extends TypographyProps {
    error?: boolean;
}

const HelperText = styled(Typography)
    .withConfig({ displayName: 'HelperText' })
    .attrs<HelperTextProps>((props) => ({
        variant: 'caption',
        color: props.error ? 'error' : 'secondary',
    }))<HelperTextProps>`
    user-select: none;
`;

export enum LabelPlacement {
    LEFT = 'left',
    RIGHT = 'right',
    TOP = 'top',
    BOTTOM = 'bottom',
}

function getLabelColor(state: { focused?: boolean; error?: boolean }) {
    if (state.error) {
        return 'error';
    }

    if (state.focused) {
        return 'primary';
    }

    return 'secondary';
}

function getLabelWrapperProps(labelPlacement: LabelPlacement): Partial<FlexProps> {
    if (labelPlacement === LabelPlacement.LEFT) {
        return { direction: 'row', alignItems: 'center', justifyContent: 'space-between' };
    }

    if (labelPlacement === LabelPlacement.RIGHT) {
        return { direction: 'row-reverse', alignItems: 'center', justifyContent: 'space-between' };
    }

    if (labelPlacement === LabelPlacement.BOTTOM) {
        return { direction: 'column-reverse' };
    }

    return { direction: 'column' };
}

interface FormControlProps extends ComponentProps<typeof Box> {
    label?: ReactElement | string;
    labelPlacement?: LabelPlacement;
    disabled?: boolean;
    error?: boolean;
    required?: boolean;
    message?: string;
    hint?: string;
    children: ReactElement;
}

export const FormControl = (props: FormControlProps) => {
    const {
        disabled,
        label,
        labelPlacement = LabelPlacement.TOP,
        error = false,
        message,
        required,
        children,
        hint,
        ...other
    } = props;
    const labelIdRef = useRef(uniqueId('form-field-'));
    const [focused, setFocused] = useState(false);

    const onFocus = useCallback(
        (event: FocusEvent<HTMLElement>) => {
            setFocused(true);
            children.props.onFocus?.(event);
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [setFocused, children.props.onFocus],
    );

    const onBlur = useCallback(
        (event: FocusEvent<HTMLElement>) => {
            setFocused(false);
            children.props.onBlur?.(event);
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [setFocused, children.props.onBlur],
    );

    return (
        <Box width="100%" {...other}>
            <Flex width="100%" spacing={0.5} {...getLabelWrapperProps(labelPlacement)}>
                <Flex direction="row" spacing={0.5}>
                    {label ? (
                        <Label
                            as="label"
                            required={required}
                            htmlFor={labelIdRef.current}
                            color={getLabelColor({ error, focused })}
                        >
                            {label}
                        </Label>
                    ) : null}
                    {hint ? <WithHint message={hint} /> : null}
                </Flex>
                {cloneElement(children, {
                    id: labelIdRef.current,
                    error,
                    disabled,
                    onFocus,
                    onBlur,
                })}
            </Flex>
            {message ? (
                <HelperText mt={0.5} error={error}>
                    {message}
                </HelperText>
            ) : null}
        </Box>
    );
};
